'use client';

import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Typography, Image, HeroIcon, Button } from '@/components/atoms';
import { getRandomDefaultImage } from '@/lib/utils/getRandomImage';

import { UserPlaylist } from '@/lib/contexts/favouriting/types';

export interface AddToPlaylistProps {
  userPlaylists: UserPlaylist[];
  addTrackToUserPlaylist: (
    playlistId: number,
    trackId: number,
  ) => Promise<void>;
  trackId: number;
  onDone: () => void;
  onCreatePlaylistClick: () => void;
}

export const AddToPlaylist = ({
  userPlaylists,
  addTrackToUserPlaylist,
  trackId,
  onDone,
  onCreatePlaylistClick,
}: AddToPlaylistProps) => {
  const t = useTranslations();
  const [loadingPlaylistId, setLoadingPlaylistId] = useState<number | null>(
    null,
  );
  const handleAddToPlaylist = async (playlistId: number) => {
    setLoadingPlaylistId(playlistId);
    await addTrackToUserPlaylist(playlistId, trackId);
    setLoadingPlaylistId(null);
    onDone();
  };
  return (
    <div className="w-full">
      <Typography.Text
        variant="body"
        size="lg"
        weight="medium"
        className="w-full text-center"
      >
        {t('SAVE_IN_OTHER_PLAYLIST')}
      </Typography.Text>
      <Button
        size="sm"
        variant="primary"
        className="w-full mt-2"
        onClick={onCreatePlaylistClick}
      >
        {t('MY_LIBRARY_PLAYLISTS_CREATE_NEW')}
      </Button>
      <ul className="space-y-3 pt-2 pb-2">
        {userPlaylists.map(playlist => (
          <li key={playlist.id}>
            <button
              className="w-full flex flex-row items-center justify-start gap-2 mt-1 cursor-pointer border-b border-transparent hover:border-b hover:border-(--color-text-primary) pb-1 text-left"
              onClick={() => handleAddToPlaylist(playlist.id)}
            >
              <Image
                alt={playlist.album_title || ''}
                src={
                  playlist.imageUrls && playlist.imageUrls.length > 0
                    ? playlist.imageUrls[0]
                    : getRandomDefaultImage()
                }
                width={40}
                height={40}
                aspectRatio="square"
                className="object-cover"
              />
              <div className="flex flex-col justify-center">
                {loadingPlaylistId === playlist.id ? (
                  <HeroIcon
                    icon={ArrowPathIcon}
                    className="h-5 w-5 animate-spin text-(--color-text-primary)"
                  />
                ) : (
                  <Typography.Text
                    variant="paragraph"
                    size="md"
                    weight="medium"
                  >
                    {playlist.album_title}
                  </Typography.Text>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
